import React, { Fragment, useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Timestamp } from "firebase/firestore";

/* UI */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Spinner,
  Slider,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@lemonadetraining/web-ui";
import { IconCirclePlus, IconCopy } from "@tabler/icons-react";

// lemonade types
import {
  WORKOUT_DATA_INIT,
  isPtExcludedFromProgrammePickersEnabled,
  weekdayOfKey,
} from "@lemonadetraining/contracts";

// -- own modules
import ProgramInfo from "./WorkoutProgram/ProgramInfo";
import CoverArtPickAndPreview from "./ProgramCard/CoverArtPickAndPreview";
import TrustNote from "./WorkoutProgram/TrustNote";
import StaticCalWeekPicker from "./../components/StaticCalWeekPicker";
import ClockNote from "./time/ClockNote";
import GymCalendarSkeleton from "./time/GymCalendarSkeleton";
import { useProgrammeCalendar } from "../hooks/useProgrammeCalendar";
import { useBoxSettings } from "../hooks/useBoxSettings";
import { useGymZone } from "../hooks/useGymZone";
import { addGymDays, gymKeySpan, gymWeekStart, shiftGymDays } from "../lib/gymClock";
import {
  loadAllWorkoutCard,
  newWorkoutCards,
  updateWorkoutCard_Backend,
  deleteWorkoutFromProgramming,
  loadWorkoutCard_DateRange,
  loadWorkoutCard_week,
  updateProgrammingWeeks,
  deleteWorkoutCard,
} from "@lemonadetraining/web-shared";
import { getUserProgramming } from "@lemonadetraining/web-shared";
import { useFeatureFlags } from "../hooks/useFeatureFlags.ts";
import * as PROGRAMMING from "@lemonadetraining/web-shared/constants/ProgrammingConst";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import ViewListOfSubscribers from "./ProgrammingView/ViewListOfSubscribers";
import WorkoutCard from "./Workout/WorkoutCard.tsx";
import TimelineControl from "./WorkoutProgram/TimelineControl.tsx";

/* ============ Workout Program ================ */
interface WorkoutProgramProps {
  programmingUID: string;
  programData: any;
  viewOnly?: boolean;
  isItMyProgramme: boolean;
  /**
   * True only for the programming's creator/owner. Gates the actions that
   * remain narrower than general edit rights: permanently deleting a card
   * (the Firestore delete rule is creator-only) and the subscribers panel
   * (the getSubscribers callable answers only the creator). Absent means
   * NOT the creator — the gate fails closed rather than inheriting the
   * broader isItMyProgramme edit right.
   */
  isCreator?: boolean;
  /** Gym staff who may read but not change this programme, under gymProgrammeEditRights. */
  staffViewOnly?: boolean;
  subInfo?: any;
}

const WorkoutProgram: React.FC<WorkoutProgramProps> = (props) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);
  const userClaims = useSelector((state: any) => state.user.claims || {});
  const programUIDRef = useRef<string>(props.programmingUID || "");
  const [workoutCards, setWorkoutCards] = useState<any[]>([]);
  const editable = !props.viewOnly;
  const isCreator = props.isCreator === true;
  const staffViewOnly = props.staffViewOnly === true;
  const [isLoading, setIsLoading] = useState(true);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [wcModal, setWcModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [copyWeekModal, setCopyWeekModal] = useState(false);
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(1);

  // New state for copy to another programming
  const [showCopyToAnother, setShowCopyToAnother] = useState(false);
  const [userProgrammings, setUserProgrammings] = useState<any[]>([]);
  const [selectedTargetProgramming, setSelectedTargetProgramming] = useState<any>(null);
  const [loadingProgrammings, setLoadingProgrammings] = useState(false);
  const { flags: featureFlags, isLoading: flagsLoading } = useFeatureFlags(copyWeekModal);
  const ptExcludedServerSide = isPtExcludedFromProgrammePickersEnabled(featureFlags);

  // Recurring dates live on the programme's calendar: its gym's, or this browser's without a gym.
  const calendar = useProgrammeCalendar(props.programData?.boxID);
  // A copy into another programme lands on that programme's own calendar.
  const targetZone = useGymZone(useBoxSettings(selectedTargetProgramming?.boxID || null).zone).zone;
  const { gym, keyOf, dateOf } = calendar;
  // Null = the card picker's browser calendar; otherwise the zone Add writes with (gym, or UTC fallback).
  const cardZone = calendar.zone !== gym.browserZone ? calendar.zone : null;

  const idx = useRef(1);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Pinned once known, so the viewed day never slides past gym midnight away from its loaded week.
  useEffect(() => {
    if (calendar.ready && selectedKey === null) setSelectedKey(calendar.todayKey);
  }, [calendar.ready, calendar.todayKey, selectedKey]);
  // The viewed day and its Monday-first week; null until the calendar is known.
  const view = calendar.ready
    ? (() => {
        const dayKey = selectedKey ?? calendar.todayKey;
        const weekStart = gymWeekStart(dayKey);
        return {
          zone: calendar.zone,
          todayKey: calendar.todayKey,
          dayKey,
          weekStart,
          weekEnd: addGymDays(weekStart, 6),
        };
      })()
    : null;
  const dayKeyOf = (date: unknown) => keyOf(date instanceof Date ? date : null);
  const inViewedWeek = (date: unknown) => {
    const key = dayKeyOf(date);
    return Boolean(view && key && key >= view.weekStart && key <= view.weekEnd);
  };

  /* Switch on Programming Type */
  const isRecurring =
    props.programData?.programmingType.type === PROGRAMMING.TYPE.reoccurring || false;
  const isFix = props.programData?.programmingType.type === PROGRAMMING.TYPE.fix || false;

  const isTimeline = props.programData?.programmingType.type === PROGRAMMING.TYPE.timeline || false;

  /* ------- sub info and calendar min and max date */
  const userSubCreatedAt = useRef<Date | null>(null);
  const subInfo = props.subInfo || null;
  const isHistoryAvailable = props.programData?.isHistoryAvailable || false;
  const releaseAll = props.programData?.releaseAll || false;
  let calMinKey: string | null = null;
  let calMaxKey: string | null = null;

  if (!props.isItMyProgramme && isRecurring && view) {
    if (subInfo?.createdAt) {
      userSubCreatedAt.current = subInfo.createdAt.toDate();
    }

    if (isHistoryAvailable) {
      if (props.programData?.createdAt) {
        calMinKey = dayKeyOf(props.programData?.createdAt.toDate());
      }
    } else {
      if (userSubCreatedAt.current) {
        calMinKey = dayKeyOf(userSubCreatedAt.current);
      }
    }

    if (!releaseAll) {
      // On Sunday the coming week opens too.
      const base = weekdayOfKey(view.todayKey) === 0 ? addGymDays(view.todayKey, 1) : view.todayKey;
      calMaxKey = addGymDays(gymWeekStart(base), 6);
    }
  }

  /* on each render show only the chosen day's workouts - if it is reoccurring */
  const todaysCards: any[] =
    isRecurring && view
      ? workoutCards.filter((card) => dayKeyOf(card.workoutData.date) === view.dayKey)
      : [];

  const viewedSpan = view ? gymKeySpan(view.weekStart, view.weekEnd, view.zone) : null;
  const spanStartMs = viewedSpan?.startMs ?? null;
  const spanEndMs = viewedSpan?.endMs ?? null;
  // Only the newest load may write the cards: a slow read of an older week is dropped.
  const loadRequest = useRef(0);

  /* loading card useEffect */
  useEffect(() => {
    if (!isLoading) return;
    // A recurring programme waits for the calendar; the week's span then starts the read.
    if (isRecurring && (spanStartMs === null || spanEndMs === null)) return;
    const request = ++loadRequest.current;
    const loadWorkouts = async () => {
      let cards: any = null;
      try {
        if (isRecurring && spanStartMs !== null && spanEndMs !== null) {
          cards = await loadWorkoutCard_DateRange(
            programUIDRef.current,
            new Date(spanStartMs),
            new Date(spanEndMs - 1)
          );
        } else if (isFix) {
          cards = await loadAllWorkoutCard(programUIDRef.current);
        } else if (isTimeline) {
          cards = await loadWorkoutCard_week(programUIDRef.current, timelineSelectedWeek.current);
        }
        if (request !== loadRequest.current) return;

        const WCs: any[] = [];
        cards.forEach((doc: any) => {
          let workoutDate = doc.data().workoutData.date;
          if (workoutDate) {
            const date = new Timestamp(
              doc.data().workoutData.date?.seconds,
              doc.data().workoutData.date?.nanoseconds
            );
            workoutDate = date.toDate();
          }

          WCs.push({
            ...doc.data(),
            workoutData: {
              ...doc.data().workoutData,
              date: workoutDate,
            },
            UID: doc.id,
          });
        });

        WCs.sort((a, b) => {
          if (!a.index) {
            return -1;
          }
          if (!b.index) {
            return 1;
          }
          return a.index - b.index;
        });

        if (WCs.length > 0 && WCs[WCs.length - 1].index) {
          idx.current = WCs[WCs.length - 1].index + 1;
        }

        setWorkoutCards(WCs);
      } catch (err) {
        console.log(err);
      } finally {
        if (request === loadRequest.current) setIsLoading(false);
      }
    };

    loadWorkouts();
  }, [isLoading, isFix, isRecurring, isTimeline, spanStartMs, spanEndMs]);

  /* --- handling calender */
  const calDateChangeHandler = (key: string) => {
    setSelectedKey(key);
    if (!view || gymWeekStart(key) !== view.weekStart) setIsLoading(true);
  };

  const viewedDayDate = () => (view ? dateOf(view.dayKey) : null);

  // ---------------------------------
  // --- handling Timeline
  // ---------------------------------
  const timelineSelectedWeek = useRef<number | null>(null);
  const weekSelected = (week: any) => {
    timelineSelectedWeek.current = week.number;
    setIsLoading(true);
  };

  const onNumberOfWeekChanged = (week: any) => {
    setLoadingIndicator(true);
    updateProgrammingWeeks(programUIDRef.current, week.number)
      .then(() => {})
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setLoadingIndicator(false);
      });
  };

  // ---------------------------------
  // --- adding / removing / editing / Copy
  // ---------------------------------
  const newWorkout = {
    UID: null,
    CoachUID: props.programData.CoachUID,
    programmingUID: programUIDRef.current,
    workoutData: WORKOUT_DATA_INIT,
    index: idx.current,
  };

  const tempWorkoutCard = useRef<any>(newWorkout);

  const onAddWorkoutHandler = () => {
    tempWorkoutCard.current = { ...newWorkout, workoutData: { ...WORKOUT_DATA_INIT } };
    tempWorkoutCard.current.programmingType = props.programData?.programmingType.type || null;
    if (isRecurring) {
      tempWorkoutCard.current.workoutData.date = viewedDayDate();
    }
    if (isFix) {
      tempWorkoutCard.current.workoutData.date = null;
    }
    if (isTimeline) {
      tempWorkoutCard.current.workoutData.date = null;
      tempWorkoutCard.current.workoutData.week = timelineSelectedWeek.current;
      tempWorkoutCard.current.workoutData.day = null;
    }
    setWcModal(true);
  };

  const onCopyWorkout = (workout: any, forceCopyToToday = false) => {
    // A copy, so the source card's own date is never touched.
    tempWorkoutCard.current = { ...newWorkout, workoutData: { ...workout.workoutData } };
    tempWorkoutCard.current.programmingType = props.programData?.programmingType.type;

    if (isRecurring) {
      tempWorkoutCard.current.workoutData.date =
        forceCopyToToday && view ? dateOf(view.todayKey) : viewedDayDate();
    }
    if (isFix) {
      tempWorkoutCard.current.workoutData.date = null;
    }
    if (isTimeline) {
      tempWorkoutCard.current.workoutData.date = null;
      tempWorkoutCard.current.workoutData.week = timelineSelectedWeek.current;
      tempWorkoutCard.current.workoutData.day = null;
    }
    setWcModal(true);
  };

  const onWorkoutCardChanged = (workoutData: any) => {
    tempWorkoutCard.current.workoutData = workoutData;
  };

  const onCreateWorkout = () => {
    setLoadingIndicator(true);
    newWorkoutCards(tempWorkoutCard.current)
      .then((ret: any) => {
        const cardUID = ret.data;
        tempWorkoutCard.current.id = cardUID;
        tempWorkoutCard.current.UID = cardUID;
        setWorkoutCards((prev) => [...prev, tempWorkoutCard.current]);
        idx.current++;
      })
      .catch((err: any) => {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: err.message,
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .finally(() => {
        setWcModal(false);
        setLoadingIndicator(false);
      });
  };

  const onUpdateWorkout = (card: any) => {
    setLoadingIndicator(true);
    updateWorkoutCard_Backend(card)
      .then(() => {
        const temp = workoutCards.map((card) => {
          if (card.UID === tempWorkoutCard.current.UID) {
            card.workoutData = tempWorkoutCard.current.workoutData;
          }
          return card;
        });

        setWorkoutCards(temp);
      })
      .catch((err: any) => {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: err.message,
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .finally(() => {
        setWcModal(false);
        setLoadingIndicator(false);
      });
  };

  const onRemoveHandler = (workout: any) => {
    tempWorkoutCard.current = workout;
    console.log("onRemoveHandler", workout);
    setDeleteModal(true);
  };
  const onRemoveWorkout = () => {
    setLoadingIndicator(true);
    deleteWorkoutFromProgramming(tempWorkoutCard.current)
      .then(() => {
        const temp = workoutCards.filter((card) => card.UID !== tempWorkoutCard.current.UID);

        setWorkoutCards(temp);
      })
      .catch((err: any) => {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: err.message,
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .finally(() => {
        setDeleteModal(false);
        setLoadingIndicator(false);
      });
  };

  const onDeleteWorkout = () => {
    setLoadingIndicator(true);
    deleteWorkoutCard(tempWorkoutCard.current.UID)
      .then(() => {
        const temp = workoutCards.filter((card) => card.UID !== tempWorkoutCard.current.UID);

        setWorkoutCards(temp);
      })
      .catch((err: any) => {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: err.message,
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .finally(() => {
        setDeleteModal(false);
        setLoadingIndicator(false);
      });
  };

  // --- Workout Modal close handle
  const handleModalClose = () => {
    setWcModal(false);
  };

  // --- are you sure to delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  // ---------------------------------
  // --- Copy Week Functionality
  // ---------------------------------
  const onCopyWeekHandler = () => {
    setSelectedWeekOffset(1);
    setCopyWeekModal(true);
  };

  const onCopyWeekToTarget = async (targetWeekOffset: number) => {
    if (!view) return;
    setLoadingIndicator(true);

    try {
      const currentWeekCards = workoutCards.filter((card) => inViewedWeek(card.workoutData.date));

      if (currentWeekCards.length === 0) {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: "No workout cards found in the current week to copy.",
        };
        dispatch(notifyActions.sendNotification(notification));
        return;
      }

      const daysDifference = targetWeekOffset * 7;

      const copyPromises = currentWeekCards.map(async (card: any) => {
        const newCard = {
          UID: null,
          CoachUID: props.programData.CoachUID,
          programmingUID: programUIDRef.current,
          workoutData: {
            ...card.workoutData,
            date: shiftGymDays(card.workoutData.date, daysDifference, view.zone),
          },
          index: idx.current++,
          programmingType: props.programData?.programmingType.type,
        };

        return newWorkoutCards(newCard);
      });

      await Promise.all(copyPromises);

      setIsLoading(true);

      const notification = {
        severity: NOTIFICATION_TYPE.success,
        message: `Successfully copied ${currentWeekCards.length} workout cards to the target week.`,
      };
      dispatch(notifyActions.sendNotification(notification));
    } catch (err: any) {
      console.error("Error copying week:", err);
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        message: err.message || "Failed to copy workout cards.",
      };
      dispatch(notifyActions.sendNotification(notification));
    } finally {
      setCopyWeekModal(false);
      setLoadingIndicator(false);
    }
  };

  const handleCloseCopyWeekModal = () => {
    setCopyWeekModal(false);
    setShowCopyToAnother(false);
    setSelectedTargetProgramming(null);
    setUserProgrammings([]);
  };

  const handleWeekOffsetChange = (value: number[]) => {
    setSelectedWeekOffset(value[0]);
  };

  // New functions for copy to another programming
  const loadUserProgrammings = async () => {
    if (!user?.uid) return;

    setLoadingProgrammings(true);
    try {
      const response = await getUserProgramming(user.uid, true, userClaims, {
        ptExcludedServerSide,
      });
      const programmings = response || [];

      console.log("Raw programming data:", programmings);

      const filteredProgrammings = programmings.filter(
        (programming: any) => programming.id !== programUIDRef.current
      );

      console.log("Filtered programming data:", filteredProgrammings);

      setUserProgrammings(filteredProgrammings);
    } catch (err) {
      console.error("Error loading user programming:", err);
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        message: "Failed to load your programming list.",
      };
      dispatch(notifyActions.sendNotification(notification));
    } finally {
      setLoadingProgrammings(false);
    }
  };

  // Waits for the flags so the target list is queried once, in its final shape.
  useEffect(() => {
    if (showCopyToAnother && !flagsLoading) loadUserProgrammings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadUserProgrammings is recreated every render
  }, [showCopyToAnother, flagsLoading]);

  const onCopyWeekToAnotherProgramming = async () => {
    if (!selectedTargetProgramming) {
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        message: "Please select a target programming.",
      };
      dispatch(notifyActions.sendNotification(notification));
      return;
    }

    if (!view || !targetZone) return;
    setLoadingIndicator(true);

    try {
      const currentWeekCards = workoutCards.filter((card) => inViewedWeek(card.workoutData.date));

      if (currentWeekCards.length === 0) {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          message: "No workout cards found in the current week to copy.",
        };
        dispatch(notifyActions.sendNotification(notification));
        return;
      }

      const daysDifference = selectedWeekOffset * 7;

      const copyPromises = currentWeekCards.map(async (card: any) => {
        const newCard = {
          UID: null,
          CoachUID: props.programData.CoachUID,
          programmingUID: selectedTargetProgramming.id,
          workoutData: {
            ...card.workoutData,
            date: shiftGymDays(card.workoutData.date, daysDifference, view.zone, targetZone),
          },
          index: card.index,
          programmingType:
            selectedTargetProgramming.programmingType?.type ||
            props.programData?.programmingType.type,
        };

        return newWorkoutCards(newCard);
      });

      await Promise.all(copyPromises);

      const weekDescription =
        selectedWeekOffset === 0 ? "same week" : `week +${selectedWeekOffset}`;

      const notification = {
        severity: NOTIFICATION_TYPE.success,
        message: `Successfully copied ${currentWeekCards.length} workout cards to ${
          selectedTargetProgramming.name || "the selected programming"
        } (${weekDescription}).`,
      };
      dispatch(notifyActions.sendNotification(notification));
    } catch (err: any) {
      console.error("Error copying week to another programming:", err);
      const notification = {
        severity: NOTIFICATION_TYPE.error,
        message: err.message || "Failed to copy workout cards to another programming.",
      };
      dispatch(notifyActions.sendNotification(notification));
    } finally {
      setCopyWeekModal(false);
      setShowCopyToAnother(false);
      setSelectedTargetProgramming(null);
      setLoadingIndicator(false);
    }
  };

  const onShowCopyToAnotherProgramming = () => {
    setLoadingProgrammings(true);
    setShowCopyToAnother(true);
  };

  const onBackToWeekOffset = () => {
    setShowCopyToAnother(false);
    setSelectedTargetProgramming(null);
  };

  const weekPicker =
    view && viewedSpan ? (
      <>
        <StaticCalWeekPicker
          todayKey={view.todayKey}
          selectedKey={view.dayKey}
          onDateChange={calDateChangeHandler}
          minKey={calMinKey}
          maxKey={calMaxKey}
        />
        <ClockNote
          variant="static"
          mode="gym"
          city={gym.city}
          differs={gym.differs([viewedSpan.startMs, viewedSpan.endMs - 1])}
          className="px-4"
        />
      </>
    ) : (
      <GymCalendarSkeleton
        className="flex flex-col gap-2 px-4 py-2"
        tileClassName="h-64 w-full rounded-md"
        testId="week-picker-loading"
      />
    );

  // --- return
  const workoutCardList = (
    <>
      {todaysCards &&
        isRecurring &&
        todaysCards.map((eachWorkout) => (
          <div key={eachWorkout.UID} className="mb-1 flex">
            <WorkoutCard
              UID={eachWorkout.UID}
              programmingUID={programUIDRef.current}
              onCopy={onCopyWorkout}
              onSaveEdit={onUpdateWorkout}
              onRemove={onRemoveHandler}
              CoachUID={eachWorkout.CoachUID}
              initialWorkoutData={eachWorkout.workoutData}
              isMyWorkout={props.isItMyProgramme}
              readOnlyView={staffViewOnly}
              programmingType={tempWorkoutCard.current.programmingType}
              gymZone={cardZone}
            />
          </div>
        ))}
      {workoutCards &&
        (isFix || isTimeline) &&
        workoutCards.map((eachWorkout) => (
          <div key={eachWorkout.UID} className="mb-1 flex">
            <WorkoutCard
              UID={eachWorkout.UID}
              programmingUID={programUIDRef.current}
              onCopy={onCopyWorkout}
              onSaveEdit={onUpdateWorkout}
              onRemove={onRemoveHandler}
              CoachUID={eachWorkout.CoachUID}
              initialWorkoutData={eachWorkout.workoutData}
              isMyWorkout={props.isItMyProgramme}
              readOnlyView={staffViewOnly}
              programmingType={tempWorkoutCard.current.programmingType}
              gymZone={cardZone}
            />
          </div>
        ))}
    </>
  );

  return (
    <Fragment>
      {copyWeekModal && (
        <Dialog open={copyWeekModal} onOpenChange={(open) => !open && handleCloseCopyWeekModal()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Copy Current Week&apos;s Workouts</DialogTitle>
              <DialogDescription>
                Choose when to copy to and whether to copy within this programming or to another
                programming.
              </DialogDescription>
            </DialogHeader>
            {/* Week Offset Slider - Always visible */}
            <div>
              <p className="mb-2 text-sm font-medium">
                Copy to:{" "}
                <span className="font-semibold">
                  {selectedWeekOffset === 0
                    ? "Same Week"
                    : `${selectedWeekOffset} Week${selectedWeekOffset > 1 ? "s" : ""} Ahead`}
                </span>
              </p>
              <div className="px-3">
                <Slider
                  value={[selectedWeekOffset]}
                  onValueChange={handleWeekOffsetChange}
                  min={0}
                  max={8}
                  step={1}
                />
                <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                  <span>0</span>
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                  <span>8</span>
                </div>
              </div>
            </div>

            <Separator className="my-3" />

            {!showCopyToAnother ? (
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={() => onCopyWeekToTarget(selectedWeekOffset)}
                  disabled={loadingIndicator || selectedWeekOffset === 0}
                >
                  {loadingIndicator ? (
                    <>
                      <Spinner className="mr-2 size-4" />
                      Copying...
                    </>
                  ) : selectedWeekOffset === 0 ? (
                    "Select a week offset"
                  ) : (
                    `To This Programming (Week +${selectedWeekOffset})`
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={onShowCopyToAnotherProgramming}
                  disabled={loadingIndicator}
                >
                  To Another Programming
                  {selectedWeekOffset === 0 ? " (Same Week)" : ` (Week +${selectedWeekOffset})`}
                </Button>
                <Button
                  variant="ghost"
                  className="text-muted-foreground w-full"
                  onClick={handleCloseCopyWeekModal}
                  disabled={loadingIndicator}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <p className="mb-2 text-sm font-bold">Copy to another programming:</p>

                {loadingProgrammings || flagsLoading ? (
                  <div className="flex justify-center py-3">
                    <Spinner className="size-4" />
                  </div>
                ) : (
                  <div className="mb-3">
                    <Select
                      value={selectedTargetProgramming?.id || undefined}
                      onValueChange={(newValue) => {
                        const selectedProgramming = userProgrammings.find(
                          (p: any) => p.id === newValue
                        );
                        setSelectedTargetProgramming(selectedProgramming);
                      }}
                      disabled={loadingIndicator}
                    >
                      <SelectTrigger className="min-h-[40px]">
                        <SelectValue placeholder="Select a programming..." />
                      </SelectTrigger>
                      <SelectContent>
                        {userProgrammings.map((programming: any) => (
                          <SelectItem key={programming.id} value={programming.id}>
                            {programming.name ||
                              programming.title ||
                              `Programming ${programming.id}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {userProgrammings.length === 0 && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        No other programming found. Create more programming to copy workouts between
                        them.
                      </p>
                    )}
                    {userProgrammings.length > 0 && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        {userProgrammings.length} programming(s) available for copying
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full"
                    onClick={onCopyWeekToAnotherProgramming}
                    disabled={loadingIndicator || !selectedTargetProgramming || !targetZone}
                  >
                    {loadingIndicator ? (
                      <>
                        <Spinner className="mr-2 size-4" />
                        Copying...
                      </>
                    ) : (
                      <>
                        Copy to {selectedTargetProgramming?.name || "Selected Programming"}
                        {selectedWeekOffset === 0
                          ? " (Same Week)"
                          : ` (Week +${selectedWeekOffset})`}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={onBackToWeekOffset}
                    disabled={loadingIndicator}
                  >
                    Back to Programming Selection
                  </Button>
                  <Button
                    variant="outline"
                    className="border-destructive text-destructive w-full"
                    onClick={handleCloseCopyWeekModal}
                    disabled={loadingIndicator}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
      {deleteModal && (
        <Dialog open={deleteModal} onOpenChange={(open) => !open && handleCloseDeleteModal()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {isCreator ? "Delete workout card from Programming?" : "Remove workout card?"}
              </DialogTitle>
              <DialogDescription>
                {isCreator
                  ? "Remove: will just remove the workout card from the programming. Delete: will delete the workout card permanently."
                  : "This removes the workout card from the programming. Only the programming's creator can delete a card permanently."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-row gap-4">
              <Button size="lg" onClick={onRemoveWorkout}>
                Remove
              </Button>
              {isCreator && (
                <Button variant="destructive" size="lg" onClick={onDeleteWorkout}>
                  Delete
                </Button>
              )}
              <Button
                variant="outline"
                className="border-destructive text-destructive"
                size="lg"
                onClick={handleCloseDeleteModal}
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {wcModal && (
        <Dialog open={wcModal} onOpenChange={(open) => !open && handleModalClose()}>
          <DialogContent className="max-h-[90vh] max-w-[95vw] sm:max-w-[650px] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Workout</DialogTitle>
            </DialogHeader>
            <WorkoutCard
              UID={tempWorkoutCard.current.UID}
              programmingUID={tempWorkoutCard.current.programmingUID}
              CoachUID={tempWorkoutCard.current.CoachUID}
              initialWorkoutData={tempWorkoutCard.current.workoutData}
              onCardUpdate={onWorkoutCardChanged}
              editable={editable}
              hideActions
              isMyWorkout={props.isItMyProgramme}
              programmingType={tempWorkoutCard.current.programmingType}
              gymZone={cardZone}
            />
            <DialogFooter className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                aria-label="Cancel"
                onClick={handleModalClose}
                disabled={loadingIndicator}
              >
                Cancel
              </Button>
              <Button
                aria-label="Create Workout"
                onClick={onCreateWorkout}
                disabled={loadingIndicator}
              >
                {loadingIndicator ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Creating...
                  </>
                ) : (
                  "Create Workout"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <TrustNote programme={props.programData} />
      {staffViewOnly ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:grid-rows-[auto_1fr]">
          <div className="min-w-0 md:col-span-8">
            <ProgramInfo
              programData={props.programData}
              isItMyProgramme={false}
              staffViewOnly
              programUID={programUIDRef.current}
            />
          </div>
          <div className="flex min-w-0 flex-col items-center gap-4 md:col-span-4 md:col-start-9 md:row-span-2 md:row-start-1">
            <CoverArtPickAndPreview
              title={props.programData?.name}
              coverArtURL={props.programData?.coverArtURL}
              coachName={props.programData?.CoachName}
              coachUID={props.programData?.CoachUID}
              pricing={props.programData?.pricing}
            />
            {isRecurring && <div className="w-full">{weekPicker}</div>}
          </div>
          <div className="min-w-0 md:col-span-8 md:col-start-1 md:row-start-2">
            {isTimeline && (
              <TimelineControl
                onWeekSelected={weekSelected}
                onNumberOfWeekChanged={onNumberOfWeekChanged}
                numberOfWeeks={props.programData?.weeks || 0}
                isItMyProgramme={props.isItMyProgramme}
              />
            )}
            {workoutCardList}
          </div>
        </div>
      ) : (
        <>
          <ProgramInfo
            programData={props.programData}
            isLoading={isLoading || loadingIndicator}
            isItMyProgramme={props.isItMyProgramme}
            programUID={programUIDRef.current}
          />
          {isTimeline && (
            <TimelineControl
              onWeekSelected={weekSelected}
              onNumberOfWeekChanged={onNumberOfWeekChanged}
              numberOfWeeks={props.programData?.weeks || 0}
              isItMyProgramme={props.isItMyProgramme}
            />
          )}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8">{workoutCardList}</div>
            <div className="col-span-4">
              <div className="flex flex-col">
                {isRecurring && (
                  <>
                    {weekPicker}
                    <Separator className="my-2" />
                  </>
                )}
                {editable && (
                  <>
                    <Button
                      variant="outline"
                      aria-label="Add Workout"
                      size="lg"
                      onClick={onAddWorkoutHandler}
                      disabled={isRecurring && !calendar.ready}
                    >
                      Workout Card
                      <IconCirclePlus className="ml-1 size-4" />
                    </Button>
                    {isRecurring && (
                      <Button
                        variant="outline"
                        aria-label="Copy Week"
                        size="lg"
                        onClick={onCopyWeekHandler}
                        disabled={!calendar.ready}
                        className="mt-2"
                      >
                        Copy Week
                        <IconCopy className="ml-1 size-4" />
                      </Button>
                    )}
                    {isCreator && (
                      <>
                        <Separator className="my-2" />
                        <ViewListOfSubscribers programmingUID={programUIDRef.current} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Fragment>
  );
};

export default WorkoutProgram;
